import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import NotificationBell from '../components/NotificationBell';
import IncomingCallBanner from '../components/IncomingCallBanner';
import {
  LayoutGrid, Calendar, Map, List, Settings as SettingsIcon,
  Users, LogOut, Menu, X, Building2, Link2, Clock, Shield, Tag as TagIcon, Star, Contact, PhoneIncoming, Gift, CalendarDays, FileText, Ban, History, ListChecks
} from 'lucide-react';

// Role-based menu visibility.
// Service: nur Tagesübersicht, Tischplan, Warteliste — bearbeiten von Reservierungen läuft über diese Ansichten.
// Manager: alles außer Mitarbeiter-Verwaltung + Tags + Einstellungen.
// Admin / Restaurantleiter / Super Admin: alles.
const ALL_NAV_ITEMS = [
  { to: '/admin', label: 'Heute', icon: LayoutGrid, testId: 'nav-dashboard', end: true, roles: ['*'] },
  { to: '/admin/week', label: 'Woche', icon: Calendar, testId: 'nav-week', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/floor-plan', label: 'Tischplan', icon: Map, testId: 'nav-floorplan', roles: ['*'] },
  { to: '/admin/reservations', label: 'Reservierungen', icon: List, testId: 'nav-reservations', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/guests', label: 'Gäste', icon: Contact, testId: 'nav-guests', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/blocked-guests', label: 'Gesperrte Gäste', icon: Ban, testId: 'nav-blocked-guests', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/calls', label: 'Anrufverlauf', icon: PhoneIncoming, testId: 'nav-calls', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/vouchers', label: 'Gutscheine', icon: Gift, testId: 'nav-vouchers', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/events', label: 'Events', icon: CalendarDays, testId: 'nav-events', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/offers', label: 'Angebote', icon: FileText, testId: 'nav-offers', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/waitlist', label: 'Warteliste', icon: Clock, testId: 'nav-waitlist', roles: ['*'] },
  { to: '/admin/checklist', label: 'Checkliste', icon: ListChecks, testId: 'nav-checklist', roles: ['*'] },
  { to: '/admin/feedback', label: 'Bewertungen', icon: Star, testId: 'nav-feedback', roles: ['super_admin', 'admin', 'restaurant_leader', 'manager'] },
  { to: '/admin/areas', label: 'Bereiche', icon: Building2, testId: 'nav-areas', roles: ['super_admin', 'admin', 'restaurant_leader'] },
  { to: '/admin/tables', label: 'Tische', icon: Users, testId: 'nav-tables', roles: ['super_admin', 'admin', 'restaurant_leader'] },
  { to: '/admin/combinations', label: 'Kombinationen', icon: Link2, testId: 'nav-combinations', roles: ['super_admin', 'admin', 'restaurant_leader'] },
  { to: '/admin/tags', label: 'Tags', icon: TagIcon, testId: 'nav-tags', roles: ['super_admin', 'admin', 'restaurant_leader'] },
  { to: '/admin/users', label: 'Mitarbeiter', icon: Shield, testId: 'nav-users', roles: ['super_admin', 'admin', 'restaurant_leader'] },
  { to: '/admin/activity-log', label: 'Aktivitäts-Log', icon: History, testId: 'nav-activity-log', roles: ['super_admin', 'admin', 'restaurant_leader'] },
  { to: '/admin/settings', label: 'Einstellungen', icon: SettingsIcon, testId: 'nav-settings', roles: ['super_admin', 'admin', 'restaurant_leader'] },
];

function visibleNavItems(role) {
  return ALL_NAV_ITEMS.filter(it => it.roles.includes('*') || it.roles.includes(role));
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = visibleNavItems(user?.role);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex" data-testid="admin-layout">
      <IncomingCallBanner />
      {/* Spacer reserving the collapsed rail width on desktop (sidebar is fixed/overlay) */}
      <div className="hidden lg:block w-16 shrink-0" aria-hidden="true" />

      {/* Sidebar - desktop: collapsed rail (icons only), expands on hover */}
      <aside
        data-testid="admin-sidebar"
        className="group hidden lg:flex fixed left-0 top-0 bottom-0 z-40 w-16 hover:w-60 bg-white border-r border-[#E5E5E0] flex-col transition-[width] duration-200 ease-in-out hover:shadow-2xl"
      >
        <div className="h-[73px] shrink-0 border-b border-[#E5E5E0] flex items-center gap-3 px-[14px] group-hover:px-3 justify-center group-hover:justify-start">
          <div className="shrink-0" data-testid="sidebar-notification-bell">
            <NotificationBell />
          </div>
          <Link to="/" className="font-brand text-[#1C1C19] whitespace-nowrap hidden group-hover:inline" data-testid="admin-logo">
            <span className="text-2xl">
              La Contadina<span className="text-[#EAB308]">.</span> <span className="text-[10px] text-[#5C5C59] font-mono uppercase tracking-wider ml-1">admin</span>
            </span>
          </Link>
        </div>
        <nav className="py-4 flex-1 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              title={item.label}
              className={({ isActive }) =>
                `flex items-center gap-3 px-0 group-hover:px-6 justify-center group-hover:justify-start py-2.5 text-sm transition-colors ${
                  isActive ? 'text-[#1C1C19] bg-[#F9F9F7] border-l-2 border-[#1C1C19]' : 'text-[#5C5C59] hover:bg-[#F9F9F7] hover:text-[#1C1C19] border-l-2 border-transparent'
                }`
              }
            >
              <item.icon className="w-5 h-5 shrink-0" />
              <span className="hidden group-hover:inline whitespace-nowrap">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 group-hover:p-4 border-t border-[#E5E5E0] shrink-0">
          <div className="mb-3 flex items-center gap-3 justify-center group-hover:justify-start group-hover:px-2">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full shrink-0" />}
            <div className="hidden group-hover:block min-w-0">
              <p className="text-sm font-medium text-[#1C1C19] truncate" data-testid="admin-user-name">{user?.name}</p>
              <p className="text-xs text-[#5C5C59] truncate">{user?.role}</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" title="Abmelden" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#5C5C59] hover:text-[#1C1C19] hover:bg-[#F9F9F7] rounded transition-colors justify-center group-hover:justify-start">
            <LogOut className="w-4 h-4 shrink-0" />
            <span className="hidden group-hover:inline">Abmelden</span>
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-[#E5E5E0] z-30 flex items-center justify-between px-4 h-14">
        <Link to="/admin" className="font-brand text-2xl" data-testid="admin-mobile-logo">La Contadina<span className="text-[#EAB308]">.</span></Link>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" className="p-2">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-[#1C1C19]/40" onClick={() => setMobileOpen(false)}>
          <nav
            className="absolute top-14 left-0 right-0 bg-white border-b border-[#E5E5E0] py-2 max-h-[calc(100vh-3.5rem)] overflow-y-auto overscroll-contain"
            onClick={e => e.stopPropagation()}
            data-testid="mobile-nav-drawer"
          >
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                data-testid={`${item.testId}-mobile`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm ${isActive ? 'text-[#1C1C19] bg-[#F9F9F7]' : 'text-[#5C5C59]'}`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
            <div className="border-t border-[#E5E5E0] mt-2 pt-2 px-6 py-3 pb-6">
              <button onClick={handleLogout} data-testid="logout-btn-mobile" className="text-sm text-[#5C5C59] flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                Abmelden
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:pl-0 pt-14 lg:pt-0 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
